import { FlowTrackerSteps, MANUAL_TRANSFER_FAQ } from 'common/constants';
import Accordion from 'components/common/Accordion/Accordion';
import {
  ATBeaconControl,
  ATBeaconNotification,
} from 'components/common/beacon/beaconRegistration';
import FlowTracker from 'components/common/FlowTracker/FlowTracker';
import React, { FC } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { setExternalAccountIncomplete } from 'src/features/transfer/transferSlice';
import { RootState } from 'src/state/store';
import { getAccountTypeDisplayName } from 'utils/accountTypeUtils';
import { formatHolderFullNames } from 'utils/accountUtils';
import { AccountType, AccountTypeDisplayNames } from 'utils/constants';
import './ExternalAccountIncomplete.scss';

interface ExternalAccountIncompleteProps {
  accountTypes: AccountType[];
  setIsStepValid: (isEnabled: boolean) => void;
  showValidation: boolean;
  setShowValidation: (isEnabled: boolean) => void;
}

const ExternalAccountIncomplete: FC<ExternalAccountIncompleteProps> = ({
  showValidation,
  setShowValidation,
  accountTypes,
}) => {
  const dispatch = useDispatch();
  const externalAccount = useSelector(
    (state: RootState) => state.transfer.selectedExternalAccount,
  );
  const externalAccountIncomplete = useSelector(
    (state: RootState) => state.transfer.externalAccountIncomplete,
  );

  const isFieldMissing = (field: string) =>
    externalAccount?.missingFields?.includes(field);

  const handleFieldChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>,
    field: 'fullName' | 'accountType' | 'accountNumber',
  ) => {
    const value = e.target.value.replace(/^\$/, '');
    dispatch(
      setExternalAccountIncomplete({
        ...externalAccountIncomplete,
        [field]: value,
      }),
    );
  };

  return (
    <div className="content-container b-grid">
      <div className="content-left b-grid-item--full-col b-xs:12-col b-sm:12-col b-md:8-col b-lg:8-col">
        <div className="b-grid external-account-incomplete">
          <div className="b-grid-item--full-col b-xs:12-col b-sm:12-col b-md:12-col b-lg:12-col">
            {showValidation && (
              <div
                id="summary"
                className="b-grid-item--full-col b-xs:12-col b-sm:12-col b-md:12-col b-lg:12-col b-padding-block-end--micro--jumbo"
              >
                <ATBeaconNotification
                  type="inline"
                  variant="warning"
                  dismissible
                  onBeaconEventDismiss={() => setShowValidation(false)}
                >
                  Please complete all fields to move forward
                </ATBeaconNotification>
              </div>
            )}
            <div className="b-grid-item--full-col b-xs:12-col b-sm:12-col b-md:12-col b-lg:12-col">
              <label className="b-typography-sans-serif--headline--100 b-font-weight-bold">
                Enter your external account information
              </label>
            </div>
            <div className="b-padding--organism-normal">
              <form
                id="automated-incomplete-transfer-form"
                noValidate
                className="b-flow-space"
              >
                {/* Financial Institution Name */}
                <ATBeaconControl>
                  <label htmlFor="institution-name" slot="label">
                    Financial institution name
                  </label>
                  <input
                    type="text"
                    name="institutionName"
                    id="institution-name"
                    value={externalAccount?.institutionName}
                    data-testid="institution-name"
                    disabled
                  />
                  <span slot="error">Please enter Financial institution name</span>
                </ATBeaconControl>

                {/* Account Owner Name */}
                <ATBeaconControl>
                  <label htmlFor="owner-name" slot="label">
                    Account owner name(s)
                  </label>
                  <input
                    type="text"
                    name="ownerName"
                    id="owner-name"
                    value={
                      (isFieldMissing('fullName')
                        ? externalAccountIncomplete?.fullName
                        : formatHolderFullNames(externalAccount?.holderFullNames)) ?? ''
                    }
                    onChange={(e) => handleFieldChange(e, 'fullName')}
                    data-testid="owner-name"
                    required={true}
                    disabled={!isFieldMissing('fullName')}
                  />
                  <span slot="error">Please enter Account owner name(s)</span>
                </ATBeaconControl>

                {/* Account Type */}
                <ATBeaconControl className="account-type-control">
                  <label htmlFor="account-type" slot="label">
                    Type of account
                  </label>
                  {!isFieldMissing('accountType') ? (
                    <input
                      type="text"
                      id="account-type"
                      data-testid="account-type-disabled"
                      name="accountType"
                      value={getAccountTypeDisplayName(
                        externalAccount?.selectedAccountType,
                      )}
                      required={true}
                      disabled
                    />
                  ) : (
                    <select
                      onChange={(e) => handleFieldChange(e, 'accountType')}
                      value={externalAccountIncomplete?.accountType ?? ''}
                      id="account-type"
                      data-testid="account-type"
                      name="accountType"
                      required={true}
                    >
                      <option key="default" value="">
                        Select
                      </option>
                      {accountTypes.map((type) => (
                        <option
                          value={AccountType[type]}
                          key={`${AccountTypeDisplayNames[type]}-dSuffix`}
                        >
                          {AccountTypeDisplayNames[type]}
                        </option>
                      ))}
                    </select>
                  )}
                  <span slot="error">
                    {!isFieldMissing('accountType')
                      ? 'Please enter Account Type'
                      : 'Please select Account Type'}
                  </span>
                </ATBeaconControl>

                {/* Account Number */}
                <ATBeaconControl>
                  <label htmlFor="account-number" slot="label">
                    Account number
                  </label>
                  <input
                    type="text"
                    name="accountNumber"
                    id="account-number"
                    value={
                      (isFieldMissing('accountNumber')
                        ? externalAccountIncomplete?.accountNumber
                        : externalAccount?.accountNumber) ?? ''
                    }
                    onChange={(e) => handleFieldChange(e, 'accountNumber')}
                    data-testid="account-number"
                    required={true}
                    disabled={!isFieldMissing('accountNumber')}
                  />
                  <span slot="error">Please enter Account number</span>
                </ATBeaconControl>

                {/* Approximate Dollar Amount */}
                <ATBeaconControl>
                  <label htmlFor="dollar-amount" slot="label">
                    Approximate dollar amount
                  </label>
                  <input
                    type="text"
                    name="balance"
                    id="dollar-amount"
                    value={`$${externalAccount?.balance?.amount ?? '0'}`}
                    data-testid="dollarAmount"
                    disabled
                  />
                  <span slot="error">Please enter Approximate dollar amount</span>
                </ATBeaconControl>
              </form>
            </div>
          </div>
        </div>
      </div>
      <div className="b-grid-item--full-col b-xs:12-col b-sm:12-col b-md:4-col b-lg:4-col">
        <div className="flow-tracker-container">
          <FlowTracker currentFlowStep={1} items={FlowTrackerSteps} />
        </div>
        <div className="faq-container">
          <Accordion items={MANUAL_TRANSFER_FAQ} />
        </div>
      </div>
    </div>
  );
};

export default ExternalAccountIncomplete;
